// @ts-nocheck -- Transitional typing, matching the surrounding deal modules.
//
// The agent's command-centre blocks.
//
// These used to compose into a single default-exported view that lived inside
// the Tasks tab. The deal-page redesign promoted the top three — what needs a
// decision, what the week holds, where the deal stands — to the page itself
// (see DealPulse), leaving the drawers to the tab. They are exported
// individually so both surfaces render the same components off the same
// derivations rather than growing a second copy of each.
import { useState } from "react";
import {
  AlertTriangle, CalendarDays, Check, ChevronDown, FileWarning, Clock, Hourglass, Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SectionCard from "@/components/common/SectionCard";
import ClientReleaseAction from "@/components/deals/tasks/ClientReleaseAction";
import { DragHandle, SortableList, SortableTask } from "@/components/deals/tasks/TaskTable";
import { formatUtcDate } from "@/utils/common.utils";
import { cn } from "@/utils/common.utils";

const TONE_STYLES = {
  rust: { dot: "bg-dash-rust", text: "text-dash-rust", Icon: AlertTriangle },
  amber: { dot: "bg-dash-amber", text: "text-dash-amber", Icon: FileWarning },
  slate: { dot: "bg-dash-slate", text: "text-dash-slate", Icon: Hourglass },
  ink: { dot: "bg-dash-greenDeep", text: "text-dash-ink", Icon: Clock },
};

export function NeedsAttention({ items, onToggleTask, onAddDocument }) {
  return (
    <SectionCard className="overflow-hidden" contentClassName="p-0">
      <header className="flex items-center gap-3 border-b border-dash-border px-5 py-4">
        <span className={cn("size-2.5 rounded-full", items.length ? "bg-dash-rust" : "bg-dash-greenMid")} aria-hidden />
        <h2 className="text-sm font-semibold text-dash-ink">Needs attention</h2>
        {items.length > 0 && (
          <span className="rounded-full bg-[#a65c4a1a] px-2 py-0.5 text-xs font-medium text-dash-rust">{items.length}</span>
        )}
      </header>
      {items.length === 0 ? (
        <div className="flex items-center gap-3 px-5 py-6" data-testid="needs-attention-clear">
          <Check className="size-5 shrink-0 text-dash-greenMid" />
          <p className="text-sm text-dash-muted">
            Nothing needs you right now. Overdue tasks and anything of yours due in the next 3 days will show up here.
          </p>
        </div>
      ) : (
        <ul data-testid="needs-attention-list">
          {items.map((item, index) => {
            const tone = TONE_STYLES[item.tone] || TONE_STYLES.ink;
            const Icon = tone.Icon;
            return (
              <li
                key={item.key}
                className={cn("flex items-center gap-3 px-5 py-3.5", index > 0 && "border-t border-dash-borderSoft")}
                data-testid={`attention-${item.kind}`}
              >
                <Icon className={cn("size-4 shrink-0", tone.text)} aria-hidden />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-dash-ink">{item.title}</p>
                  <p className={cn("truncate text-xs", tone.text)}>{item.detail}</p>
                </div>
                {/* Who owes it, as its own column. An agent scanning this list
                    is deciding who to chase, and that decision was previously
                    only inferable from the detail line on the waiting rows. */}
                {item.owner && (
                  <span
                    className={cn(
                      "hidden shrink-0 truncate text-xs sm:block sm:max-w-[9rem]",
                      item.owner.state === "named" ? "text-dash-muted" : "text-dash-faint",
                    )}
                    data-testid={`attention-owner-${item.kind}`}
                  >
                    {item.owner.label}
                  </span>
                )}
                {/* One button, and it resolves the thing in place — the brief's
                    "instant resolution buttons. Zero clutter." */}
                {item.task && (
                  <Button
                    type="button"
                    variant="dashboardOutline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => onToggleTask(item.task)}
                    data-testid={`attention-resolve-${item.task.id}`}
                  >
                    <Check data-icon="inline-start" /> Done
                  </Button>
                )}
                {item.kind === "missing_document" && (
                  <Button
                    type="button"
                    variant="dashboardOutline"
                    size="sm"
                    className="shrink-0"
                    onClick={() => onAddDocument(item)}
                    data-testid="attention-add-document"
                  >
                    Upload
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </SectionCard>
  );
}

export function ThisWeek({ events }) {
  return (
    <SectionCard className="overflow-hidden" contentClassName="p-0">
      <header className="flex items-center gap-3 border-b border-dash-border px-5 py-4">
        <CalendarDays className="size-4 text-dash-muted" aria-hidden />
        <h2 className="text-sm font-semibold text-dash-ink">This week</h2>
      </header>
      {events.length === 0 ? (
        <p className="px-5 py-6 text-sm text-dash-muted" data-testid="this-week-empty">
          Nothing scheduled in the next seven days.
        </p>
      ) : (
        <ol className="flex flex-col" data-testid="this-week-list">
          {events.map((event, index) => (
            <li
              key={event.key}
              className={cn("flex items-center gap-3 px-5 py-3", index > 0 && "border-t border-dash-borderSoft")}
            >
              <span
                className={cn("w-14 shrink-0 text-xs font-semibold",
                  event.days === 0 ? "text-dash-rust" : "text-dash-faint")}
              >
                {event.days === 0 ? "Today" : event.days === 1 ? "Tomorrow" : `${event.days} days`}
              </span>
              <span className={cn("size-1.5 shrink-0 rounded-full",
                event.kind === "milestone" ? "bg-dash-greenDeep" : "bg-dash-border")} aria-hidden />
              <span className="min-w-0 flex-1 truncate text-sm text-dash-ink">{event.title}</span>
              {event.date && (
                <span className="hidden shrink-0 text-xs text-dash-faint sm:inline">
                  {formatUtcDate(event.date)}
                </span>
              )}
            </li>
          ))}
        </ol>
      )}
    </SectionCard>
  );
}

export function StagePipeline({ pipeline, onAdvancePhase, advancing, variant = "list" }) {
  // The deal page shows the same steps as a horizontal rail, which is what the
  // redesign asks for; the drawer view keeps the vertical list. Same data, same
  // `buildStagePipeline` derivation — only the axis differs, so the two cannot
  // disagree about which stage a deal is in.
  if (variant === "rail") return <StageRail pipeline={pipeline} />;
  return (
    <SectionCard className="overflow-hidden" contentClassName="p-0">
      <header className="flex items-center justify-between gap-3 border-b border-dash-border px-5 py-4">
        <h2 className="text-sm font-semibold text-dash-ink">Stage</h2>
        {onAdvancePhase && pipeline.nextPhase && (
          <Button type="button" variant="dashboardOutline" size="sm" onClick={onAdvancePhase} loading={advancing} data-testid="pipeline-advance">
            Advance
          </Button>
        )}
      </header>
      <ol className="flex flex-col gap-0 px-5 py-4" data-testid="stage-pipeline">
        {pipeline.steps.map((step, index) => (
          <li key={step.phase} className="flex items-stretch gap-3">
            <div className="flex w-4 flex-col items-center">
              <span
                className={cn("mt-1 size-3 shrink-0 rounded-full border-2",
                  step.state === "done" && "border-dash-greenDeep bg-dash-greenDeep",
                  step.state === "current" && "border-dash-greenDeep bg-white",
                  step.state === "future" && "border-dash-border bg-white")}
                aria-hidden
              />
              {index < pipeline.steps.length - 1 && (
                <span className={cn("w-0.5 flex-1",
                  step.state === "done" ? "bg-dash-greenDeep/40" : "bg-dash-border")} aria-hidden />
              )}
            </div>
            <div className={cn("pb-4", step.state === "future" && "opacity-45")}>
              <p className={cn("text-sm", step.state === "current" ? "font-semibold text-dash-ink" : "text-dash-muted")}>
                {step.label}
              </p>
              {step.state === "current" && <p className="text-xs text-dash-faint">Where the deal is now</p>}
            </div>
          </li>
        ))}
      </ol>
    </SectionCard>
  );
}

export function TaskGroups({ groups, onToggleTask, onEditTask, onReleaseTask, onConfirmTask, clientActionTaskId, onReorder = null }) {
  return (
    <div className="flex flex-col gap-3" data-testid="task-groups">
      {groups.map((group) => (
        <TaskGroup
          key={group.key}
          group={group}
          onToggleTask={onToggleTask}
          onEditTask={onEditTask}
          onReleaseTask={onReleaseTask}
          onConfirmTask={onConfirmTask}
          clientActionTaskId={clientActionTaskId}
          onReorder={onReorder}
        />
      ))}
    </div>
  );
}

// Each drawer drags on its own: the reorder endpoint lets the listed rows trade
// the slots they already hold, so moving rows inside one drawer never shifts a
// row in another.
function TaskGroup({ group, onToggleTask, onEditTask, onReleaseTask, onConfirmTask, clientActionTaskId, onReorder }) {
  const [open, setOpen] = useState(group.defaultOpen);
  const empty = group.tasks.length === 0;
  const ids = group.tasks.map((task) => task.id);
  return (
    <SectionCard className="overflow-hidden" contentClassName="p-0">
      <button
        type="button"
        onClick={() => !empty && setOpen((current) => !current)}
        className={cn("flex w-full items-center gap-3 px-5 py-3.5 text-left transition-colors",
          empty ? "cursor-default" : "hover:bg-dash-hover/50")}
        aria-expanded={open && !empty}
        disabled={empty}
        data-testid={`task-group-${group.key}`}
      >
        <ChevronDown
          className={cn("size-4 shrink-0 text-dash-faint transition-transform",
            !open && "-rotate-90", empty && "opacity-25")}
          aria-hidden
        />
        <span className="text-sm font-medium text-dash-ink">{group.label}</span>
        <span className="ml-auto text-xs text-dash-faint">{group.tasks.length}</span>
      </button>
      {open && !empty && (
        <SortableList ids={ids} onReorder={onReorder}>
        <ul className="border-t border-dash-borderSoft">
          {group.tasks.map((task, index) => {
            const complete = task.status === "complete";
            return (
              <SortableTask key={task.id} id={task.id} disabled={!onReorder}>
              {({ ref, style, handle }) => (
              <li
                ref={ref}
                style={style}
                className={cn("flex flex-wrap items-center gap-x-3 gap-y-2 bg-white px-4 py-3 sm:px-5", index > 0 && "border-t border-dash-borderSoft")}
              >
                {onReorder && <DragHandle handle={handle} title={task.title} />}
                {/* No priority pill, no blank due-date dash, no phase column —
                    the three things the brief called out as clutter. */}
                <button
                  type="button"
                  onClick={() => onToggleTask(task)}
                  className={cn("flex size-4 shrink-0 items-center justify-center rounded border transition-colors",
                    complete ? "border-dash-greenMid bg-dash-greenMid text-white" : "border-dash-muted hover:border-dash-greenDeep")}
                  aria-label={`Mark ${task.title} as ${complete ? "not completed" : "completed"}`}
                  data-testid={`group-toggle-${task.id}`}
                >
                  {complete && <Check className="size-3" />}
                </button>
                <span className={cn("min-w-[8rem] flex-1 truncate text-sm",
                  complete ? "text-dash-faint line-through" : "text-dash-ink")}>
                  {task.title}
                </span>
                {task.due_date && (
                  <span className="shrink-0 text-xs text-dash-faint">{formatUtcDate(task.due_date)}</span>
                )}
                {onEditTask && (
                  <button
                    type="button"
                    onClick={() => onEditTask(task)}
                    className="shrink-0 rounded p-1 text-dash-faint transition-colors hover:bg-dash-hover hover:text-dash-ink"
                    aria-label={`Edit ${task.title}`}
                    data-testid={`group-edit-${task.id}`}
                  >
                    <Pencil className="size-3.5" />
                  </button>
                )}
                <ClientReleaseAction
                  task={task}
                  onRelease={onReleaseTask}
                  onConfirm={onConfirmTask}
                  working={clientActionTaskId === task.id}
                />
              </li>
              )}
              </SortableTask>
            );
          })}
        </ul>
        </SortableList>
      )}
    </SectionCard>
  );
}

/**
 * The stage rail across the deal page.
 *
 * Deliberately the deal's own *phases* rather than the under-contract
 * sub-stages the mockup sketches. Phases are real data — they drive the Advance
 * action and the seeded checklist — while "Inspections / Financing" as rail
 * stops would be a second, invented progress model that nothing updates. A
 * buyer deal still in pre-approval renders a meaningful rail this way; under a
 * sub-stage model it would render an empty one.
 */
function StageRail({ pipeline }) {
  return (
    <SectionCard contentClassName="px-5 py-5">
      <ol className="flex flex-col gap-3 md:flex-row md:items-start md:gap-2" data-testid="stage-rail">
        {pipeline.steps.map((step, index) => {
          const last = index === pipeline.steps.length - 1;
          return (
            <li key={step.phase} className={cn("flex min-w-0 items-start gap-2", !last && "md:flex-1")}>
              <div className="flex shrink-0 flex-col items-center gap-2">
                <span
                  className={cn(
                    "flex size-7 items-center justify-center rounded-full border-2 transition-colors",
                    step.state === "done" && "border-dash-greenDeep bg-dash-greenDeep text-white",
                    step.state === "current" && "border-dash-greenDeep bg-dash-greenDeep ring-4 ring-dash-greenDeep/15",
                    step.state === "future" && "border-dash-border bg-white",
                  )}
                  aria-hidden
                >
                  {step.state === "done" && <Check className="size-3.5" />}
                </span>
              </div>
              <div className="min-w-0 pt-0.5">
                <p className={cn("truncate text-sm",
                  step.state === "future" ? "text-dash-faint" : "font-medium text-dash-ink")}>
                  {step.label}
                </p>
                <p className="truncate text-xs text-dash-faint">
                  {step.state === "current" ? "Current"
                    : step.state === "done" ? "Done"
                    : index === pipeline.currentIndex + 1 ? "Next" : ""}
                </p>
              </div>
              {!last && (
                <span
                  className={cn("mt-3.5 hidden h-px min-w-4 flex-1 md:block",
                    step.state === "done" ? "bg-dash-greenDeep" : "bg-dash-border")}
                  aria-hidden
                />
              )}
            </li>
          );
        })}
      </ol>
    </SectionCard>
  );
}
